import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Headphones, Volume2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

const binauralPresets = [
  { 
    name: "Deep Sleep", 
    baseFreq: 200, 
    beatFreq: 2, 
    description: "Delta waves (0.5-4 Hz) for deep restorative sleep",
    color: "#6366F1"
  },
  { 
    name: "Meditation", 
    baseFreq: 200, 
    beatFreq: 6, 
    description: "Theta waves (4-8 Hz) for deep meditation",
    color: "#8B5CF6"
  },
  { 
    name: "Relaxation", 
    baseFreq: 200, 
    beatFreq: 10, 
    description: "Alpha waves (8-12 Hz) for calm relaxation",
    color: "#06B6D4"
  },
  { 
    name: "Focus", 
    baseFreq: 200, 
    beatFreq: 14, 
    description: "Low Beta (12-15 Hz) for focused concentration",
    color: "#10B981"
  },
  { 
    name: "Alertness", 
    baseFreq: 200, 
    beatFreq: 20, 
    description: "Beta waves (15-30 Hz) for active thinking",
    color: "#F59E0B"
  },
  { 
    name: "Peak Performance", 
    baseFreq: 200, 
    beatFreq: 40, 
    description: "Gamma waves (30-100 Hz) for peak mental state",
    color: "#EF4444"
  },
];

const BinauralBeats = ({ audioContext, isPlaying }) => {
  const [activePreset, setActivePreset] = useState(null);
  const [baseFrequency, setBaseFrequency] = useState(200);
  const [beatFrequency, setBeatFrequency] = useState(10);
  const [volume, setVolume] = useState(50);
  const [customMode, setCustomMode] = useState(false);
  
  const leftOscRef = useRef(null);
  const rightOscRef = useRef(null);
  const leftGainRef = useRef(null);
  const rightGainRef = useRef(null);
  const mergerRef = useRef(null);

  const startBinaural = useCallback(() => {
    if (!audioContext) return;

    // Stop existing
    stopBinaural();

    const leftOsc = audioContext.createOscillator();
    const rightOsc = audioContext.createOscillator();
    const leftGain = audioContext.createGain();
    const rightGain = audioContext.createGain();
    const merger = audioContext.createChannelMerger(2);

    leftOsc.frequency.setValueAtTime(baseFrequency, audioContext.currentTime);
    rightOsc.frequency.setValueAtTime(baseFrequency + beatFrequency, audioContext.currentTime);
    
    leftGain.gain.setValueAtTime(volume / 100, audioContext.currentTime);
    rightGain.gain.setValueAtTime(volume / 100, audioContext.currentTime);

    leftOsc.connect(leftGain);
    rightOsc.connect(rightGain);
    leftGain.connect(merger, 0, 0);
    rightGain.connect(merger, 0, 1);
    merger.connect(audioContext.destination);

    leftOsc.start();
    rightOsc.start();

    leftOscRef.current = leftOsc;
    rightOscRef.current = rightOsc;
    leftGainRef.current = leftGain;
    rightGainRef.current = rightGain;
    mergerRef.current = merger;
  }, [audioContext, baseFrequency, beatFrequency, volume]);

  const stopBinaural = useCallback(() => {
    [leftOscRef, rightOscRef].forEach(ref => {
      if (ref.current) {
        try {
          ref.current.stop();
          ref.current.disconnect();
        } catch (e) {}
        ref.current = null;
      }
    });
    [leftGainRef, rightGainRef, mergerRef].forEach(ref => {
      if (ref.current) {
        try {
          ref.current.disconnect();
        } catch (e) {}
        ref.current = null;
      }
    });
  }, []);

  useEffect(() => {
    if ((activePreset || customMode) && isPlaying) {
      startBinaural();
    } else {
      stopBinaural();
    }

    return () => stopBinaural();
  }, [activePreset, customMode, isPlaying, startBinaural, stopBinaural]);

  useEffect(() => {
    if (leftOscRef.current && rightOscRef.current && audioContext) {
      leftOscRef.current.frequency.setValueAtTime(baseFrequency, audioContext.currentTime);
      rightOscRef.current.frequency.setValueAtTime(baseFrequency + beatFrequency, audioContext.currentTime);
    }
  }, [baseFrequency, beatFrequency, audioContext]);

  useEffect(() => {
    if (leftGainRef.current && rightGainRef.current && audioContext) {
      leftGainRef.current.gain.setValueAtTime(volume / 100, audioContext.currentTime);
      rightGainRef.current.gain.setValueAtTime(volume / 100, audioContext.currentTime);
    }
  }, [volume, audioContext]);

  const selectPreset = (preset) => {
    if (activePreset?.name === preset.name) {
      setActivePreset(null);
      setCustomMode(false);
    } else {
      setActivePreset(preset);
      setBaseFrequency(preset.baseFreq);
      setBeatFrequency(preset.beatFreq);
      setCustomMode(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Headphones size={16} className="text-cyan-400" />
          <h4 className="text-sm font-medium text-gray-300">Binaural Beats</h4>
          <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">
            Headphones Required
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Volume2 size={14} className="text-gray-500" />
          <Slider
            value={[volume]}
            onValueChange={([v]) => setVolume(v)}
            min={0}
            max={100}
            step={1}
            data-testid="binaural-volume"
            className="w-20"
          />
        </div>
      </div>

      {/* Presets Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {binauralPresets.map((preset) => (
          <motion.button
            key={preset.name}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => selectPreset(preset)}
            data-testid={`binaural-${preset.name.toLowerCase().replace(' ', '-')}`}
            className={cn(
              "p-3 rounded-xl border text-left transition-all duration-200",
              activePreset?.name === preset.name
                ? "border-cyan-500 bg-cyan-500/20"
                : "border-white/10 bg-white/5 hover:border-white/20"
            )}
          >
            <div className="flex items-center gap-2 mb-1">
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: preset.color }}
              />
              <span className="text-sm font-medium text-white">{preset.name}</span>
            </div>
            <p className="text-xs text-gray-500 font-mono">{preset.beatFreq} Hz</p>
          </motion.button>
        ))}
      </div>

      {/* Custom Controls */}
      <div className="glass-card p-4 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-400">Custom Binaural</span>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setCustomMode(!customMode);
              setActivePreset(null);
            }}
            data-testid="binaural-custom-toggle"
            className={cn(
              "px-3 py-1 text-xs rounded-lg transition-colors",
              customMode
                ? "bg-cyan-500 text-white"
                : "bg-white/5 text-gray-400 hover:bg-white/10"
            )}
          >
            {customMode ? "ON" : "OFF"}
          </motion.button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-gray-500">Base Frequency</label>
              <span className="text-xs text-violet-400 font-mono">{baseFrequency} Hz</span>
            </div>
            <Slider
              value={[baseFrequency]}
              onValueChange={([v]) => {
                setBaseFrequency(v);
                if (!customMode) setCustomMode(true);
                setActivePreset(null);
              }}
              min={100}
              max={500}
              step={1}
              data-testid="binaural-base-freq"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-gray-500">Beat Frequency</label>
              <span className="text-xs text-cyan-400 font-mono">{beatFrequency} Hz</span>
            </div>
            <Slider
              value={[beatFrequency]}
              onValueChange={([v]) => {
                setBeatFrequency(v);
                if (!customMode) setCustomMode(true);
                setActivePreset(null);
              }}
              min={0.5}
              max={40}
              step={0.5}
              data-testid="binaural-beat-freq"
            />
          </div>
        </div>

        <div className="text-xs text-gray-500">
          Left ear: {baseFrequency} Hz • Right ear: {baseFrequency + beatFrequency} Hz
        </div>
      </div>
    </div>
  );
};

export default BinauralBeats;
